import { api } from '../../api/endpoints'
import { ApiError, errorMessage } from '../../api/client'
import type { ChannelEvent, ChecklistReasonInput, Message, MessageOption } from '../../api/types'
import { rememberReaction } from '../../lib/recentReactions'
import { toast } from '../../state/toasts'
import { enqueueMessage } from '../../sync/outbox'
import { receiveEvent, syncChannel } from '../../sync/syncEngine'
import { checklistSummary } from './questions'

async function run(action: () => Promise<ChannelEvent | undefined>, failure: string): Promise<boolean> {
  try {
    await receiveEvent(await action())
    return true
  } catch (error) {
    toast(`${failure}. ${errorMessage(error)}`)
    return false
  }
}

export function toggleReaction(message: Message, emoji: string, meId: string) {
  const mine = message.reactions.some((r) => r.emoji === emoji && r.userIds.includes(meId))
  if (mine) return run(() => api.removeReaction(message.id, emoji), "Couldn't remove the reaction")
  rememberReaction(emoji)
  return run(() => api.addReaction(message.id, emoji), "Couldn't add the reaction")
}

export const editMessage = (id: string, body: string) => run(() => api.editMessage(id, body), "Couldn't edit the message")
export const deleteMessage = (id: string) => run(() => api.deleteMessage(id), "Couldn't delete the message")
export const pinMessage = (id: string) => run(() => api.pin(id), "Couldn't pin the message")
export const unpinMessage = (id: string) => run(() => api.unpin(id), "Couldn't unpin the message")

/** Tapping one of a bot's options: its label is sent as your reply to the question, through the outbox like any message. */
export function answerQuestion(question: Message, option: MessageOption) {
  return enqueueMessage({
    channelId: question.channelId,
    body: option.label,
    replyToId: question.id,
    answer: { messageId: question.id, optionId: option.id },
  })
}

/** Ticks or unticks an item of a bot's checklist. False when it could not be saved: the row goes back, and the app says why. */
export async function setTick(checklist: Message, itemId: string, ticked: boolean): Promise<boolean> {
  try {
    await receiveEvent(await (ticked ? api.tick(checklist.id, itemId) : api.untick(checklist.id, itemId)))
    return true
  } catch (error) {
    const closed = error instanceof ApiError && (error.error === 'question_closed' || error.error === 'question_answered')
    toast(closed ? 'This checklist has closed' : "Couldn't save that tick")
    if (closed) void syncChannel(checklist.channelId)
    return false
  }
}

/** Done on a bot's checklist: the summary goes as your reply through the outbox, with a reason for each item not ticked. */
export function finishChecklist(checklist: Message, reasons: ChecklistReasonInput[]) {
  return enqueueMessage({
    channelId: checklist.channelId,
    body: checklistSummary(checklist, reasons),
    replyToId: checklist.id,
    answer: { messageId: checklist.id, optionId: null },
    reasons,
  })
}
